import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { EmbeddedMessage, SMS, WhatsApp } from 'constants/messageMediums';
import {
  InAppContentDefinition,
  MESSAGE_FORMAT,
  MessageMedium,
  SMSContentDefinition,
} from 'modules/graphql/types';
import { usePlacementSelectorContext } from 'modules/placements/context/PlacementSelectorContext';
import { selectIsSendMmsOn } from 'core/userContext/selectors';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { BottomTooltip } from 'ui/tooltip';
import { Edit16, WarningFill } from 'ui/icons';
import { spacing } from 'ui/theme';

import { useSMSLinkShortening } from '../EditSMS/context/SMSLinkShorteningContext';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { useSaveTemplate } from '../hooks/useSaveTemplate';
import { LostProgressModal } from '../modals/LostProgressModal';
import { DetailsSaveButtonWrapper, UnsavedChangesMessage } from './EditorContentContainer.styled';
import { displayContent } from './content';
import { MessagingPermissionOptions } from './types';
import { useGetCanManageBaseTemplates, useGetMessagingPermission } from './utils';
import { useWhatsAppLinkShortening } from '../EditWhatsApp/context/WhatsAppLinkShorteningContext';
import { useSmsSendersWithESP } from '../hooks/useSmsSendersWithESP';

const unsavedModalContent = {
  header: 'Unsaved changes',
  body: 'This template has been edited. Do you want to save your changes?',
  cancel: 'Cancel',
  leave: 'Continue without saving',
  save: 'Save and continue',
};

const getLostProgressModalContent = (messageFormat: MESSAGE_FORMAT) => {
  let { includedSelection, deletedSelection, updatedSelection, header, save } = {
    includedSelection: '',
    deletedSelection: '',
    updatedSelection: '',
    header: '',
    save: '',
  };

  if (messageFormat === MESSAGE_FORMAT.Json) {
    header = 'HTML message will be lost';
    includedSelection = 'raw data (JSON)';
    deletedSelection = 'content';
    updatedSelection = 'Content, styles, and layout (HTML)';
    save = 'Save template and lose HTML message';
  }
  if (messageFormat === MESSAGE_FORMAT.Html) {
    header = 'Custom data will be lost';
    includedSelection = 'content (HTML)';
    deletedSelection = 'raw data';
    updatedSelection = 'Raw data (JSON)';
    save = 'Save template and lose custom data';
  }

  return {
    header,
    body: `Because your message is configured to include ${includedSelection}, saving the template will delete the ${deletedSelection} you've entered. To keep it, update the message to include Everything or ${updatedSelection}`,
    cancel: 'Cancel',
    save,
  };
};

export const ButtonContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

const EditIconAndText = (): JSX.Element => (
  <ButtonContentWrapper data-test={'edit-button'}>
    <Edit16 />
    {displayContent.btnEdit}
  </ButtonContentWrapper>
);

export const DetailsEditSaveButton = () => {
  const { refetch, medium } = useTemplateQueryContext();
  const { campaignId, experimentFlow: isExperimentFlow } = useQueryParamContext();
  const { initialTemplate, isDirty, isEditMode, setIsEditMode, liveTemplate } =
    useEditableFieldsContext();
  const { saveTemplate } = useSaveTemplate();
  const { resetIsSMSLinkShorteningEnabled } = useSMSLinkShortening();
  const { resetIsWhatsAppLinkShorteningEnabled } = useWhatsAppLinkShortening();
  const sendMmsEnabled = useSelector(selectIsSendMmsOn);
  const isCampaignFlow = !!campaignId;
  const messagingPermission = useGetMessagingPermission();
  const canManageBaseTemplates = useGetCanManageBaseTemplates();
  const { isSelectedPlacementArchived } = usePlacementSelectorContext();
  const smsSendersWithESP = useSmsSendersWithESP();

  const getButtonStateAndTooltipContent = (): {
    isButtonDisabled: boolean;
    tooltipContent: string;
  } => {
    if (isCampaignFlow || isExperimentFlow)
      return {
        isButtonDisabled: messagingPermission === MessagingPermissionOptions.VIEW_ONLY,
        tooltipContent: displayContent.tooltipViewOnlyPermission,
      };

    if (medium === EmbeddedMessage)
      return {
        isButtonDisabled: isSelectedPlacementArchived,
        tooltipContent: displayContent.tooltipArchivedPlacement,
      };

    if (medium === SMS) {
      const smsData = (liveTemplate.mediumData || {}) as SMSContentDefinition;
      const { imageUrl, verifiedContactCardId } = smsData;
      return {
        isButtonDisabled:
          !!(imageUrl || verifiedContactCardId) &&
          !sendMmsEnabled &&
          !!smsSendersWithESP[smsData?.smsSender?.id]?.esp?.isSenderIterable,
        tooltipContent: displayContent.tooltipMmsNotAvailable,
      };
    }

    return {
      isButtonDisabled: !canManageBaseTemplates,
      tooltipContent: displayContent.tooltipManageBaseTemplatesPermission,
    };
  };
  const { isButtonDisabled, tooltipContent } = getButtonStateAndTooltipContent();

  const { mediumData: currentMediumData = {} } = liveTemplate || {};
  const { mediumData: initialMediumData = {} } = initialTemplate || {};
  const { messageFormat: currentMessageFormat } = currentMediumData as InAppContentDefinition;
  const {
    messageFormat: initialMessageFormat,
    html: initialHtml,
    payload: initialPayload,
  } = initialMediumData as InAppContentDefinition;

  const [lostProgressModalVisible, setLostProgressModalVisible] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const handleOnClick = useCallback(() => {
    if (isEditMode) {
      if (
        medium === MessageMedium.InApp &&
        currentMessageFormat !== MESSAGE_FORMAT.Everything &&
        currentMessageFormat !== initialMessageFormat
      ) {
        const hasHtmlContent = !!initialHtml?.length;
        const hasJsonContent =
          initialPayload !== null && !!initialPayload?.length && initialPayload !== '{}';

        if (currentMessageFormat === MESSAGE_FORMAT.Json && hasHtmlContent) {
          setLostProgressModalVisible(true);
        } else if (currentMessageFormat === MESSAGE_FORMAT.Html && hasJsonContent) {
          setLostProgressModalVisible(true);
        } else {
          saveTemplate();
        }
      } else {
        saveTemplate();
      }
    } else {
      setIsEditMode(!isEditMode);
    }
  }, [currentMessageFormat, initialMessageFormat, isEditMode, medium, saveTemplate, setIsEditMode]);

  const handleCancel = () => {
    if (isDirty) {
      setModalVisible(true);
    } else {
      setIsEditMode(!isEditMode);
    }
  };

  const onClose = () => {
    setModalVisible(false);
  };

  const onLeave = () => {
    setModalVisible(false);
    setIsEditMode(false);
    refetch();
    if (medium === MessageMedium.SMS) {
      resetIsSMSLinkShorteningEnabled();
    }
    if (medium === WhatsApp) {
      resetIsWhatsAppLinkShorteningEnabled();
    }
  };

  const onSave = () => {
    setModalVisible(false);
    handleOnClick();
  };

  const onCloseLostProgress = () => {
    setLostProgressModalVisible(false);
  };

  const onSaveLostProgress = () => {
    saveTemplate();
    setLostProgressModalVisible(false);
  };

  return (
    <BottomTooltip isEnabled={isButtonDisabled} tooltipContent={tooltipContent}>
      <DetailsSaveButtonWrapper data-test="details-button">
        {isEditMode && isDirty && (
          <UnsavedChangesMessage data-test={'unsaved-changes-message'}>
            <WarningFill isColorInherited={true} />
            {displayContent.strUnsavedChanges}
          </UnsavedChangesMessage>
        )}
        {isEditMode && (
          <Button
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.SECONDARY}
            onClick={handleCancel}
          >
            {displayContent.btnCancel}
          </Button>
        )}
        <Button
          data-testid="details-button-non-edit-mode"
          isDisabled={isButtonDisabled}
          onClick={handleOnClick}
          size={BUTTON_SIZES.SMALL}
          variant={isEditMode ? BUTTON_VARIANTS.PRIMARY : BUTTON_VARIANTS.SECONDARY}
        >
          {isEditMode ? displayContent.btnSave : <EditIconAndText />}
        </Button>
      </DetailsSaveButtonWrapper>
      {isDirty && modalVisible && (
        <LostProgressModal
          onClose={onClose}
          onLeave={onLeave}
          onSave={onSave}
          isModalOpen={modalVisible}
          modalContent={unsavedModalContent}
        />
      )}

      {lostProgressModalVisible && currentMessageFormat && (
        <LostProgressModal
          onClose={onCloseLostProgress}
          onSave={onSaveLostProgress}
          modalContent={getLostProgressModalContent(currentMessageFormat)}
          isModalOpen={lostProgressModalVisible}
        />
      )}
    </BottomTooltip>
  );
};
